import { EntityAdapter, EntityState, createEntityAdapter } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import { AppExceptionModel } from '../../models/app-exception.model';

import { Album } from '../../models/album.model';
import * as albumActions from '../actions/album.actions';

export interface AlbumState extends EntityState<Album> {
  loading: boolean;
  loaded: boolean;
  inProgress: boolean;
  error: AppExceptionModel | undefined;
}

export const albumAdapter: EntityAdapter<Album> = createEntityAdapter<Album>();

export const initialState: AlbumState = albumAdapter.getInitialState({
  loading: false,
  loaded: false,
  inProgress: false,
  error: undefined,
});

export const albumReducer = createReducer(
  initialState,
  on(albumActions.getAlbums, (state, {}) => ({
    ...state,
    loading: true,
    loaded: false,
    error: undefined,
  })),
  on(albumActions.getAlbum, (state, {}) => ({
    ...state,
    error: undefined,
  })),
  on(albumActions.addOneToStore, (state, { album }) =>
    albumAdapter.upsertOne(album, {
      ...state,
      error: undefined,
      loading: false,
      loaded: true,
    }),
  ),
  on(albumActions.addManyToStore, (state, { albums }) => {
    return albumAdapter.upsertMany(albums, {
      ...state,
      loading: false,
      loaded: true,
      inProgress: false,
      error: undefined,
    });
  }),
  on(albumActions.getAlbumsFailure, (state, { error }) => ({
    ...state,
    loading: false,
    loaded: true,
    error: error,
  })),
  on(albumActions.createAlbum, (state, { album }) => ({
    ...state,
    error: undefined,
    inProgress: true,
  })),
  on(albumActions.createSuccess, (state, { album }) =>
    albumAdapter.upsertOne(album, {
      ...state,
      error: undefined,
      inProgress: false,
    }),
  ),
  on(albumActions.createFailure, (state, { error }) => ({
    ...state,
    error: error,
    inProgress: false,
  })),
  on(albumActions.updateAlbum, (state, { album }) => ({
    ...state,
    error: undefined,
    inProgress: true,
  })),
  on(albumActions.updateSuccess, (state, { album }) =>
    albumAdapter.upsertOne(album, {
      ...state,
      error: undefined,
      inProgress: false,
    }),
  ),
  on(albumActions.updateFailure, (state, { error }) => ({
    ...state,
    error: error,
    inProgress: false,
  })),
  on(albumActions.deleteAlbum, (state, { id }) => ({
    ...state,
    error: undefined,
    inProgress: true,
  })),
  on(albumActions.deleteSuccess, (state, { id }) =>
    albumAdapter.removeOne(id, {
      ...state,
      error: undefined,
      inProgress: false,
    }),
  ),
  on(albumActions.deleteFailure, (state, { error }) => ({
    ...state,
    error: error,
    inProgress: false,
  })),
  on(albumActions.clearFromStore, (state, { ids }) =>
    albumAdapter.removeMany(ids, {
      ...state,
      error: undefined,
      inProgress: false,
    }),
  ),
  on(albumActions.clear, (state) => initialState),
);
